/**
 * TikTokPixelEditor - Configuration editor for TikTok Pixel
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Link,
  Typography,
} from '@mui/material';
import type { TikTokPixelConfig, PlatformConfig } from '../../types/analytics.types';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';

interface TikTokPixelEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

export const TikTokPixelEditor: React.FC<TikTokPixelEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const ttConfig = config as TikTokPixelConfig;
  const [localConfig, setLocalConfig] = useState<TikTokPixelConfig>(ttConfig);
  const [localName, setLocalName] = useState(name || '');

  useEffect(() => {
    setLocalConfig(ttConfig);
    setLocalName(name || '');
  }, [ttConfig, name]);

  const handleChange = useCallback(
    (field: keyof TikTokPixelConfig | 'advancedMatching.email' | 'advancedMatching.phone', value: unknown) => {
      let newConfig: TikTokPixelConfig;

      if (field === 'advancedMatching.email') {
        newConfig = {
          ...localConfig,
          advancedMatching: {
            ...localConfig.advancedMatching,
            email: value as boolean,
          },
        };
      } else if (field === 'advancedMatching.phone') {
        newConfig = {
          ...localConfig,
          advancedMatching: {
            ...localConfig.advancedMatching,
            phone: value as boolean,
          },
        };
      } else {
        newConfig = { ...localConfig, [field]: value };
      }

      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  return (
    <Box>
      <TextField
        label="Pixel ID"
        value={localConfig.pixelId || ''}
        onChange={(e) => handleChange('pixelId', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        placeholder="CXXXXXXXXXXXXXXXXX"
        helperText={
          <>
            Find your Pixel ID in{' '}
            <Link href="https://ads.tiktok.com/marketing_api/apps" target="_blank" rel="noopener">
              TikTok Ads Manager → Events
            </Link>
          </>
        }
        slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.editorField('tiktok_pixel', 'pixelId') } }}
      />

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Advanced Matching (optional)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Enable to improve attribution accuracy
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={localConfig.advancedMatching?.email ?? false}
            onChange={(e) => handleChange('advancedMatching.email', e.target.checked)}
            disabled={disabled || readOnly}
          />
        }
        label="Match by email"
        sx={{ display: 'block' }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={localConfig.advancedMatching?.phone ?? false}
            onChange={(e) => handleChange('advancedMatching.phone', e.target.checked)}
            disabled={disabled || readOnly}
          />
        }
        label="Match by phone"
      />

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.pixelId}
            data-testid={ANALYTICS_SETTINGS_TEST_IDS.editorSave('tiktok_pixel')}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
